import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DEFAULT_CONFIG } from '../../config/defaultConfig';
import { getApiUrl, getPlatformApiUrl } from '../../utils/styleHelpers';
import { LuZap, LuKey, LuLock, LuCrown, LuCheck, LuShoppingCart, LuCreditCard, LuHistory, LuTrash2, LuShieldCheck, LuSlidersHorizontal, LuX, LuFlaskConical } from 'react-icons/lu';

/**
 * Edit Quota & Annual Unlimited Pass Manager
 * Enforces monthly edit quotas, credit top-ups, and annual unlimited subscriptions.
 */
export const EditQuotaManager = ({ config, onSave }) => {
    const modules = config?.modules || DEFAULT_CONFIG.modules || {};
    const quotaMod = modules.editQuota || DEFAULT_CONFIG.modules?.editQuota || {
        id: 'editQuota',
        enabled: true,
        planTier: 'monthly_free', // 'monthly_free' | 'pro_monthly' | 'annual_unlimited'
        monthlyLimit: 100,
        editsUsedThisMonth: 0,
        bonusCredits: 0,
        billingCycleAnchor: new Date().toISOString(),
        annualUnlimitedExpires: null,
        transactionHistory: [
            { id: 'tx_init', type: 'quota_grant', description: 'Initial Edit Allowance (100 Edits)', amount: 100, date: new Date().toISOString() }
        ]
    };

    const isEnabled = true;
    const planTier = quotaMod.planTier || 'monthly_free';
    const monthlyLimit = Math.max(100, Number(quotaMod.monthlyLimit) || 100);
    const usedEdits = quotaMod.editsUsedThisMonth || 0;
    const bonusCredits = quotaMod.bonusCredits || 0;
    const isUnlimited = planTier === 'annual_unlimited';

    const [paymentInfo, setPaymentInfo] = useState(null);

    useEffect(() => {
        const fetchPaymentInfo = async () => {
            try {
                const res = await axios.get(getPlatformApiUrl('api_ecommerce.php?action=platform-payment-public'));
                if (res.data && typeof res.data === 'object') {
                    setPaymentInfo(res.data);
                    if (res.data.gateway && res.data.gateway !== 'simulated') {
                        setPaymentMethod(res.data.gateway);
                    }
                }
            } catch (e) {}
        };
        fetchPaymentInfo();
    }, []);

    const remainingMonthly = Math.max(0, monthlyLimit - usedEdits);
    const totalAvailable = isUnlimited ? 'Unlimited' : (remainingMonthly + bonusCredits);
    const usagePercent = isUnlimited ? 0 : Math.min(100, Math.round((usedEdits / monthlyLimit) * 100));

    const [purchaseSuccessMsg, setPurchaseSuccessMsg] = useState('');
    const [selectedPack, setSelectedPack] = useState('pack_200');
    const [checkoutModalItem, setCheckoutModalItem] = useState(null); // { type, edits, price, label }
    const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' | 'paypal' | 'sandbox'
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [cardNumber, setCardNumber] = useState('•••• •••• •••• 4242');
    const [cardExpiry, setCardExpiry] = useState('12/28');
    const [cardCvc, setCardCvc] = useState('123');
    const [devConfigOpen, setDevConfigOpen] = useState(false);

    // Admin Passcode State
    const [inputPasscode, setInputPasscode] = useState('');
    const [passcodeError, setPasscodeError] = useState('');
    const [passcodeSuccess, setPasscodeSuccess] = useState('');

    const handleRedeemPasscode = (codeToTry = inputPasscode) => {
        setPasscodeError('');
        setPasscodeSuccess('');
        const result = redeemAdminPasscode(config, codeToTry);
        if (result.success) {
            onSave(result.updatedConfig, { skipQuotaCheck: true });
            setPasscodeSuccess(result.message);
            setPurchaseSuccessMsg(result.message);
            setInputPasscode('');
            setTimeout(() => {
                setPasscodeSuccess('');
                setPurchaseSuccessMsg('');
            }, 6000);
        } else {
            setPasscodeError(result.error);
        }
    };

    const handleQuickAdminUnlock = () => {
        handleRedeemPasscode('OMNIVASO-UNLIMITED');
    };

    const masterBilling = quotaMod.masterPlatformBilling || {
        developerStripePublishableKey: '',
        developerStripeCheckoutUrl: '',
        developerPaypalEmail: '',
        developerBankPayoutInfo: ''
    };

    const updateQuotaModule = (updates) => {
        const updatedConfig = {
            ...config,
            modules: {
                ...modules,
                editQuota: {
                    ...quotaMod,
                    ...updates
                }
            }
        };
        onSave(updatedConfig, { skipQuotaCheck: true });
    };

    const toggleModule = () => {
        updateQuotaModule({ enabled: !isEnabled });
    };

    // Reset Plan Tier to 100 Free Edits (Clears test annual unlimited pass & bonus credits)
    const handleResetPlanTier = () => {
        try {
            localStorage.removeItem('omnivaso_demo_draft');
            localStorage.removeItem('omnivaso_cached_config');
            sessionStorage.clear();
        } catch (e) {}

        updateQuotaModule({
            planTier: 'monthly_free',
            monthlyLimit: 100,
            editsUsedThisMonth: 0,
            bonusCredits: 0,
            annualUnlimitedExpires: null,
            transactionHistory: [
                { id: 'tx_init', type: 'quota_grant', description: 'Initial Edit Allowance (100 Edits)', amount: 100, date: new Date().toISOString() }
            ]
        });
        setPurchaseSuccessMsg('✨ Plan reset back to Base Free Tier (100 Edits).');
        setTimeout(() => setPurchaseSuccessMsg(''), 4000);
    };

    // Top-Up Credit Packs Definition
    const creditPacks = [
        { id: 'pack_40', edits: 40, price: '$5.00', label: '40 Edit Credits', tag: 'Starter' },
        { id: 'pack_200', edits: 200, price: '$18.00', label: '200 Edit Credits', tag: 'Popular (Save 36%)' },
        { id: 'pack_800', edits: 800, price: '$50.00', label: '800 Edit Credits', tag: 'Best Value' }
    ];

    // Open Checkout Modal
    const triggerCheckout = (item) => {
        setCheckoutModalItem(item);
    };

    // Process Payment Completion
    const handleCompletePayment = () => {
        if (!checkoutModalItem) return;
        setIsProcessingPayment(true);

        setTimeout(() => {
            setIsProcessingPayment(false);

            if (paymentMethod === 'sandbox') {
                setPurchaseSuccessMsg(`🧪 Test Mode Payment Simulated: Checkout flow verified! (Sandbox mode — no real credits applied to live site)`);
                setCheckoutModalItem(null);
                setTimeout(() => setPurchaseSuccessMsg(''), 5000);
                return;
            }

            if (checkoutModalItem.type === 'credits') {
                const newBonus = bonusCredits + checkoutModalItem.edits;
                const newHistory = [
                    {
                        id: 'tx_' + Date.now(),
                        type: 'credit_purchase',
                        description: `Purchased ${checkoutModalItem.label} (${checkoutModalItem.price}) via ${paymentMethod.toUpperCase()}`,
                        amount: checkoutModalItem.edits,
                        date: new Date().toISOString()
                    },
                    ...(quotaMod.transactionHistory || [])
                ];

                updateQuotaModule({
                    bonusCredits: newBonus,
                    transactionHistory: newHistory
                });

                setPurchaseSuccessMsg(`🎉 Payment successful! Added +${checkoutModalItem.edits} Edit Credits to your balance.`);
            } else if (checkoutModalItem.type === 'annual') {
                const nextYear = new Date();
                nextYear.setFullYear(nextYear.getFullYear() + 1);

                const newHistory = [
                    {
                        id: 'tx_' + Date.now(),
                        type: 'annual_upgrade',
                        description: `Purchased 1-Year Unlimited Edits Pass ($199/yr) via ${paymentMethod.toUpperCase()}`,
                        amount: '∞',
                        date: new Date().toISOString()
                    },
                    ...(quotaMod.transactionHistory || [])
                ];

                updateQuotaModule({
                    planTier: 'annual_unlimited',
                    annualUnlimitedExpires: nextYear.toISOString(),
                    transactionHistory: newHistory
                });

                setPurchaseSuccessMsg('🏆 Payment successful! Upgraded your site to Annual Unlimited Edits!');
            }

            setCheckoutModalItem(null);
            setTimeout(() => setPurchaseSuccessMsg(''), 6000);
        }, 1200);
    };

    // Remove single transaction
    const handleDeleteTransaction = (txId) => {
        const updated = (quotaMod.transactionHistory || []).filter(tx => tx.id !== txId);
        updateQuotaModule({ transactionHistory: updated });
    };

    // Clear all transaction history
    const handleClearTransactionHistory = () => {
        updateQuotaModule({
            transactionHistory: [
                { id: 'tx_init', type: 'quota_grant', description: 'Initial Monthly Edit Allowance (100 Edits)', amount: 100, date: new Date().toISOString() }
            ]
        });
    };

    // Admin Quota Reset
    const handleResetMonthlyCounter = () => {
        updateQuotaModule({
            editsUsedThisMonth: 0,
            billingCycleAnchor: new Date().toISOString()
        });
        setPurchaseSuccessMsg('🔄 Monthly edit counter reset to 0.');
        setTimeout(() => setPurchaseSuccessMsg(''), 4000);
    };

    // Admin Full Reset: Clears tier back to monthly_free, removes bonus credits, resets usage to 0, resets history
    const handleFullResetQuotaState = () => {
        if (window.confirm('Reset Edit Quota to clean default (Monthly Free: 100 Edits, 0 used, 0 bonus credits, no active passes)?')) {
            updateQuotaModule({
                planTier: 'monthly_free',
                monthlyLimit: 100,
                editsUsedThisMonth: 0,
                bonusCredits: 0,
                annualUnlimitedExpires: null,
                transactionHistory: [
                    { id: 'tx_init', type: 'quota_grant', description: 'Initial Monthly Edit Allowance (100 Edits)', amount: 100, date: new Date().toISOString() }
                ]
            });
            setPurchaseSuccessMsg('✨ Quota system reset back to clean defaults (100 Monthly Edits).');
            setTimeout(() => setPurchaseSuccessMsg(''), 4000);
        }
    };

    return (
        <div style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '14px', overflow: 'hidden', fontFamily: 'Inter, system-ui, sans-serif' }}>
            {/* Header */}
            <div style={{
                padding: '24px',
                background: isEnabled ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' : '#f8fafc',
                color: isEnabled ? '#ffffff' : '#64748b',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px',
                borderBottom: '1px solid #e2e8f0'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><LuZap size={32} style={{ color: '#60a5fa' }} /></div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h4 style={{ margin: 0, fontSize: '1.25rem', color: isEnabled ? '#ffffff' : '#0f172a', fontWeight: '800' }}>
                                Edit Quota, Credit Store & Annual Pass Engine
                            </h4>
                            <span style={{
                                background: isUnlimited ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : (isEnabled ? '#3b82f6' : '#94a3b8'),
                                color: '#ffffff',
                                padding: '3px 10px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '800',
                                textTransform: 'uppercase'
                            }}>
                                {isUnlimited ? 'ANNUAL UNLIMITED PASS' : (planTier === 'pro_monthly' ? 'PRO MONTHLY' : 'FREE TIER')}
                            </span>
                        </div>
                        <div style={{ fontSize: '0.82rem', color: isEnabled ? '#94a3b8' : '#64748b', marginTop: '4px' }}>
                            Module ID: <code>editQuota</code> &bull; Controls edit allowances, extra credit purchases, and annual pass verification.
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                        background: 'rgba(16, 185, 129, 0.2)',
                        color: '#6ee7b7',
                        border: '1px solid #10b981',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '0.82rem',
                        fontWeight: 'bold'
                    }}>
                        ● Quota System Active
                    </span>
                </div>
            </div>

            {purchaseSuccessMsg && (
                <div style={{ background: '#dcfce7', color: '#15803d', borderBottom: '1px solid #bbf7d0', padding: '14px 24px', fontSize: '0.9rem', fontWeight: '700' }}>
                    {purchaseSuccessMsg}
                </div>
            )}

            {!isUnlimited && typeof totalAvailable === 'number' && totalAvailable <= 20 && (
                <div style={{
                    background: totalAvailable <= 5 ? '#fef2f2' : (totalAvailable <= 10 ? '#fffbe6' : '#f0f9ff'),
                    color: totalAvailable <= 5 ? '#dc2626' : (totalAvailable <= 10 ? '#d97706' : '#0369a1'),
                    borderBottom: `1px solid ${totalAvailable <= 5 ? '#fecaca' : (totalAvailable <= 10 ? '#fef08a' : '#bae6fd')}`,
                    padding: '12px 24px',
                    fontSize: '0.88rem',
                    fontWeight: '800',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                }}>
                    <span>
                        {totalAvailable <= 5 ? '🚨 CRITICAL EDIT QUOTA ALERT: Only 5 edits remaining! You will soon be blocked from saving changes.' :
                         (totalAvailable <= 10 ? '⚠️ LOW EDIT QUOTA WARNING: Only 10 edits remaining in your quota balance.' :
                          'ℹ️ EDIT QUOTA NOTICE: You have 20 edit credits remaining for this billing cycle.')}
                    </span>
                    <button
                        onClick={() => {
                            const inputEl = document.querySelector('input[placeholder="Enter passcode..."]');
                            if (inputEl) {
                                inputEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                inputEl.focus();
                            }
                        }}
                        style={{
                            background: totalAvailable <= 5 ? '#dc2626' : '#0f172a',
                            color: '#ffffff',
                            border: 'none',
                            padding: '6px 14px',
                            borderRadius: '8px',
                            fontSize: '0.78rem',
                            fontWeight: '800',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        <LuKey size={13} /> Redeem Code / Upgrade
                    </button>
                </div>
            )}

            <div style={{ padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: '28px' }}>
                    {/* SECTION 1: USAGE METER & DASHBOARD */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
                        {/* Box 1: Base Edit Allowance */}
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '20px' }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '6px' }}>
                                Base Edit Allowance
                            </div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#0f172a' }}>
                                {isUnlimited ? 'Unlimited' : `${usedEdits} / ${monthlyLimit}`}
                                <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', marginLeft: '6px' }}>edits used</span>
                            </div>

                            {!isUnlimited && (
                                <div style={{ marginTop: '12px' }}>
                                    <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                        <div style={{
                                            width: `${usagePercent}%`,
                                            height: '100%',
                                            background: usagePercent > 90 ? '#ef4444' : (usagePercent > 70 ? '#f59e0b' : '#10b981'),
                                            transition: 'width 0.3s'
                                        }}></div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#64748b', marginTop: '6px' }}>
                                        <span>{remainingMonthly} base edits remaining</span>
                                        <span>{usagePercent}% Used</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Box 2: Top-Up Credit Balance */}
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '14px', padding: '20px' }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#1e40af', textTransform: 'uppercase', marginBottom: '6px' }}>
                                Purchased Top-Up Credits
                            </div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#1e3a8a' }}>
                                +{bonusCredits} <span style={{ fontSize: '0.85rem', fontWeight: '600' }}>credits</span>
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#3b82f6', marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <LuLock size={12} /> Credits never expire and carry forward indefinitely.
                            </div>
                        </div>

                        {/* Box 3: Total Editing Capacity */}
                        <div style={{ background: isUnlimited ? '#fef3c7' : '#f0fdf4', border: `1px solid ${isUnlimited ? '#fcd34d' : '#bbf7d0'}`, borderRadius: '14px', padding: '20px' }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: '700', color: isUnlimited ? '#92400e' : '#166534', textTransform: 'uppercase', marginBottom: '6px' }}>
                                Total Available Edits
                            </div>
                            <div style={{ fontSize: '1.8rem', fontWeight: '900', color: isUnlimited ? '#b45309' : '#15803d' }}>
                                {totalAvailable}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: isUnlimited ? '#b45309' : '#16a34a', marginTop: '8px' }}>
                                {isUnlimited ? 'Active Annual Pass (Expires 1 Year)' : 'Combined Base Allowance + Purchased Credits'}
                            </div>
                        </div>
                    </div>

                    {/* SECTION 2: ANNUAL UNLIMITED PASS BANNER */}
                    <div style={{
                        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
                        borderRadius: '16px',
                        padding: '28px',
                        color: '#ffffff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '20px',
                        boxShadow: '0 10px 25px -5px rgba(67, 56, 202, 0.4)'
                    }}>
                        <div style={{ maxWidth: '540px' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(245, 158, 11, 0.2)', border: '1px solid rgba(245, 158, 11, 0.5)', color: '#fbbf24', padding: '4px 12px', borderRadius: '16px', fontSize: '0.75rem', fontWeight: '800', marginBottom: '10px' }}>
                                <LuCrown size={13} /> UNLIMITED PASS OPTION
                            </div>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.4rem', fontWeight: '800' }}>
                                Upgrade to 1-Year Unlimited Edits Pass
                            </h3>
                            <p style={{ margin: 0, fontSize: '0.92rem', color: '#c7d2fe', lineHeight: '1.5' }}>
                                Never worry about monthly edit limits or credit top-ups again. Save 60% compared to monthly credit packs and edit your site endlessly.
                            </p>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '2.2rem', fontWeight: '900', color: '#ffffff' }}>
                                $199 <span style={{ fontSize: '1rem', color: '#a5b4fc', fontWeight: '600' }}>/ year</span>
                            </div>
                            {isUnlimited ? (
                                <div style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid #10b981', color: '#6ee7b7', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', marginTop: '10px', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                    <LuCheck size={14} /> Active Annual Subscription
                                </div>
                            ) : (
                                <button
                                    onClick={() => triggerCheckout({ type: 'annual', label: '1-Year Unlimited Edits Pass', price: '$199.00' })}
                                    style={{
                                        marginTop: '10px',
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: '#ffffff',
                                        border: 'none',
                                        padding: '12px 24px',
                                        borderRadius: '12px',
                                        fontWeight: '800',
                                        fontSize: '1rem',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 14px rgba(245, 158, 11, 0.4)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <LuZap size={14} /> Upgrade to Annual Pass ($199)
                                </button>
                            )}
                        </div>
                    </div>

                    {/* SECTION 2B: ADMIN PASSCODE & PROMO CODE UNLOCK */}
                    <div style={{
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        borderRadius: '16px',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <LuKey size={22} style={{ color: '#2563eb' }} />
                            <div>
                                <h4 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a', fontWeight: '800' }}>
                                    Redeem Admin Passcode or VIP Promo Code
                                </h4>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                value={inputPasscode}
                                onChange={(e) => { setInputPasscode(e.target.value); setPasscodeError(''); }}
                                placeholder="Enter passcode..."
                                style={{
                                    flex: 1,
                                    minWidth: '240px',
                                    padding: '12px 16px',
                                    borderRadius: '10px',
                                    border: passcodeError ? '2px solid #ef4444' : '1px solid #cbd5e1',
                                    fontSize: '0.95rem',
                                    fontWeight: '700',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    background: '#ffffff',
                                    color: '#0f172a'
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleRedeemPasscode(); }}
                            />
                            <button
                                type="button"
                                onClick={() => handleRedeemPasscode()}
                                style={{
                                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '10px',
                                    fontWeight: '800',
                                    fontSize: '0.92rem',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <LuKey size={14} /> Redeem Code
                            </button>
                        </div>

                        {passcodeError && (
                            <div style={{ color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '700' }}>
                                {passcodeError}
                            </div>
                        )}

                        {passcodeSuccess && (
                            <div style={{ color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '700' }}>
                                {passcodeSuccess}
                            </div>
                        )}
                    </div>

                    {/* SECTION 3: PURCHASE TOP-UP CREDIT PACKS */}
                    <div>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '1.1rem', color: '#0f172a', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <LuShoppingCart size={18} /> Purchase Additional Top-Up Edit Credits
                        </h4>
                        <p style={{ margin: '0 0 16px 0', fontSize: '0.88rem', color: '#64748b' }}>
                            Need extra edits for a big project? Top-up credits are added instantly to your balance and never expire.
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                            {creditPacks.map((pack) => (
                                <div
                                    key={pack.id}
                                    style={{
                                        border: `2px solid ${selectedPack === pack.id ? '#3b82f6' : '#e2e8f0'}`,
                                        background: selectedPack === pack.id ? '#eff6ff' : '#ffffff',
                                        borderRadius: '14px',
                                        padding: '20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => setSelectedPack(pack.id)}
                                >
                                    <div>
                                        <div style={{ display: 'inline-block', background: '#e0f2fe', color: '#0369a1', fontSize: '0.72rem', fontWeight: '800', padding: '2px 8px', borderRadius: '10px', marginBottom: '8px' }}>
                                            {pack.tag}
                                        </div>
                                        <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#0f172a' }}>
                                            {pack.label}
                                        </div>
                                        <div style={{ fontSize: '1.6rem', fontWeight: '900', color: '#3b82f6', margin: '8px 0' }}>
                                            {pack.price}
                                        </div>
                                    </div>

                                    <button
                                        onClick={(e) => {
                                             e.stopPropagation();
                                            triggerCheckout({ type: 'credits', edits: pack.edits, label: pack.label, price: pack.price });
                                        }}
                                        style={{
                                            marginTop: '12px',
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            background: '#3b82f6',
                                            color: '#ffffff',
                                            border: 'none',
                                            fontWeight: '700',
                                            fontSize: '0.9rem',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <LuCreditCard size={14} /> Checkout ({pack.price})
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* SECTION 4: AUDIT HISTORY LEDGER */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0, fontSize: '1rem', color: '#0f172a', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <LuHistory size={16} /> Quota & Credit Transaction History
                            </h4>
                            {(quotaMod.transactionHistory || []).length > 1 && (
                                <button
                                    onClick={handleClearTransactionHistory}
                                    style={{
                                        background: '#fee2e2',
                                        color: '#ef4444',
                                        border: '1px solid #fca5a5',
                                        padding: '4px 10px',
                                        borderRadius: '6px',
                                        fontSize: '0.78rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    <LuTrash2 size={13} /> Reset / Clear History
                                </button>
                            )}
                        </div>
                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            <table style={{ width: '100%', minWidth: '540px', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: '#f1f5f9', color: '#475569' }}>
                                        <th style={{ padding: '10px 14px' }}>Date</th>
                                        <th style={{ padding: '10px 14px' }}>Transaction Description</th>
                                        <th style={{ padding: '10px 14px' }}>Type</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'right' }}>Credit Change</th>
                                        <th style={{ padding: '10px 14px', textAlign: 'center', width: '50px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(quotaMod.transactionHistory || []).map((tx, i) => (
                                        <tr key={tx.id || i} style={{ borderTop: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '10px 14px', color: '#64748b' }}>
                                                {new Date(tx.date).toLocaleDateString()} {new Date(tx.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </td>
                                            <td style={{ padding: '10px 14px', fontWeight: '600', color: '#0f172a' }}>
                                                {tx.description}
                                            </td>
                                            <td style={{ padding: '10px 14px' }}>
                                                <span style={{
                                                    background: tx.type === 'annual_upgrade' ? '#fef3c7' : (tx.type === 'credit_purchase' ? '#dcfce7' : '#f1f5f9'),
                                                    color: tx.type === 'annual_upgrade' ? '#b45309' : (tx.type === 'credit_purchase' ? '#15803d' : '#475569'),
                                                    padding: '2px 8px',
                                                    borderRadius: '6px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '700'
                                                }}>
                                                    {tx.type}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '800', color: tx.amount === '∞' ? '#f59e0b' : '#10b981' }}>
                                                {typeof tx.amount === 'number' ? `+${tx.amount}` : tx.amount}
                                            </td>
                                            <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                {tx.id !== 'tx_init' && (
                                                    <button
                                                        onClick={() => handleDeleteTransaction(tx.id)}
                                                        title="Delete transaction log entry"
                                                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', opacity: 0.7, padding: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                    >
                                                        <LuTrash2 size={13} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

            {/* CHECKOUT POPUP MODAL */}
            {checkoutModalItem && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 99999,
                    background: 'rgba(15, 23, 42, 0.75)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '20px',
                        maxWidth: '480px',
                        width: '100%',
                        overflow: 'hidden',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
                        animation: 'modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                    }}>
                        <div style={{ background: '#0f172a', color: '#ffffff', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <LuShieldCheck size={22} style={{ color: '#38bdf8' }} />
                                <div>
                                    <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800' }}>Omnivaso Secure Checkout</h4>
                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>256-bit SSL Encrypted Payment</div>
                                </div>
                            </div>
                            <button
                                onClick={() => setCheckoutModalItem(null)}
                                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <LuX size={18} />
                            </button>
                        </div>

                        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {/* Developer Payout Notice */}
                            <div style={{ background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: '10px', padding: '12px 14px', fontSize: '0.82rem', color: '#0369a1', lineHeight: '1.4' }}>
                                <b>Platform Subscription Payment</b>: This transaction pays for system editing credits directly to the site software provider (Developer Account).
                            </div>

                            {/* Summary Box */}
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>Selected Item</div>
                                    <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>{checkoutModalItem.label}</div>
                                </div>
                                <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#3b82f6' }}>{checkoutModalItem.price}</div>
                            </div>

                            {/* Payment Method Selector */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>
                                    Select Payment Gateway / Method
                                </label>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {(paymentInfo?.enable_stripe !== false && (!paymentInfo?.hide_unconfigured || paymentInfo?.stripe_public || paymentInfo?.gateway === 'stripe')) && (
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('stripe')}
                                            style={{
                                                flex: '1 1 120px',
                                                border: `2px solid ${paymentMethod === 'stripe' ? '#3b82f6' : '#cbd5e1'}`,
                                                background: paymentMethod === 'stripe' ? '#eff6ff' : '#ffffff',
                                                borderRadius: '10px',
                                                padding: '10px 8px',
                                                cursor: 'pointer',
                                                fontWeight: '700',
                                                fontSize: '0.85rem',
                                                color: '#0f172a',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <LuCreditCard size={14} /> Stripe
                                        </button>
                                    )}
                                    {(paymentInfo?.enable_square !== false && (!paymentInfo?.hide_unconfigured || paymentInfo?.square_application_id || paymentInfo?.gateway === 'square')) && (
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('square')}
                                            style={{
                                                flex: '1 1 120px',
                                                border: `2px solid ${paymentMethod === 'square' ? '#3b82f6' : '#cbd5e1'}`,
                                                background: paymentMethod === 'square' ? '#eff6ff' : '#ffffff',
                                                borderRadius: '10px',
                                                padding: '10px 8px',
                                                cursor: 'pointer',
                                                fontWeight: '700',
                                                fontSize: '0.85rem',
                                                color: '#0f172a',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <span style={{ fontSize: '1rem', lineHeight: 1 }}>⬛</span> Square
                                        </button>
                                    )}
                                    {(paymentInfo?.enable_paypal !== false && (!paymentInfo?.hide_unconfigured || paymentInfo?.paypal_client_id || paymentInfo?.gateway === 'paypal')) && (
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('paypal')}
                                            style={{
                                                flex: '1 1 120px',
                                                border: `2px solid ${paymentMethod === 'paypal' ? '#3b82f6' : '#cbd5e1'}`,
                                                background: paymentMethod === 'paypal' ? '#eff6ff' : '#ffffff',
                                                borderRadius: '10px',
                                                padding: '10px 8px',
                                                cursor: 'pointer',
                                                fontWeight: '700',
                                                fontSize: '0.85rem',
                                                color: '#0f172a',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            PayPal
                                        </button>
                                    )}
                                    {(paymentInfo?.enable_simulated || paymentInfo?.gateway === 'simulated' || !paymentInfo?.hide_unconfigured || (!paymentInfo?.stripe_public && !paymentInfo?.square_application_id && !paymentInfo?.paypal_client_id)) && (
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod('sandbox')}
                                            style={{
                                                flex: '1 1 120px',
                                                border: `2px solid ${paymentMethod === 'sandbox' ? '#3b82f6' : '#cbd5e1'}`,
                                                background: paymentMethod === 'sandbox' ? '#eff6ff' : '#ffffff',
                                                borderRadius: '10px',
                                                padding: '10px 8px',
                                                cursor: 'pointer',
                                                fontWeight: '700',
                                                fontSize: '0.85rem',
                                                color: '#0f172a',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <LuSlidersHorizontal size={14} /> Test Mode
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Card Input Form */}
                            {paymentMethod !== 'paypal' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>Card Number</label>
                                        <input
                                            type="text"
                                            value={cardNumber}
                                            onChange={e => setCardNumber(e.target.value)}
                                            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', fontFamily: 'monospace' }}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>Expiration</label>
                                            <input
                                                type="text"
                                                value={cardExpiry}
                                                onChange={e => setCardExpiry(e.target.value)}
                                                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', fontFamily: 'monospace' }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>CVC / CVV</label>
                                            <input
                                                type="text"
                                                value={cardCvc}
                                                onChange={e => setCardCvc(e.target.value)}
                                                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', fontFamily: 'monospace' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '16px', borderRadius: '10px', color: '#166534', fontSize: '0.88rem' }}>
                                    You will be redirected to PayPal Express to authorize your payment of <b>{checkoutModalItem.price}</b> safely.
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                onClick={handleCompletePayment}
                                disabled={isProcessingPayment}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    borderRadius: '12px',
                                    background: isProcessingPayment ? '#94a3b8' : '#10b981',
                                    color: '#ffffff',
                                    border: 'none',
                                    fontWeight: '800',
                                    fontSize: '1.05rem',
                                    cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)'
                                }}
                            >
                                {isProcessingPayment ? '⏳ Processing Payment...' : `Authorize & Pay ${checkoutModalItem.price}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Global helper function to redeem an Admin Passcode or Promo Code to unlock unlimited edits.
 * Returns { success: boolean, code?: string, updatedConfig?: object, message?: string, error?: string }
 */
export const redeemAdminPasscode = (config, inputCode) => {
    if (!inputCode || typeof inputCode !== 'string') {
        return { success: false, error: 'Please enter a valid passcode.' };
    }
    const cleanCode = inputCode.trim().toUpperCase();
    const modules = config?.modules || DEFAULT_CONFIG.modules || {};
    const quotaMod = modules.editQuota || DEFAULT_CONFIG.modules?.editQuota || {};
    const customCode = (quotaMod.adminUnlockCode || quotaMod.masterPlatformBilling?.customUnlockCode || '').toUpperCase();
    const redeemedCodes = quotaMod.redeemedCodes || [];

    // Check if passcode has already been used once
    if (redeemedCodes.includes(cleanCode)) {
        return {
            success: false,
            error: `❌ Passcode Expired. Code "${cleanCode}" has already been redeemed and cannot be used again.`
        };
    }

    const validCodes = [
        'OMNIVASO-UNLIMITED',
        'UNLIMITED2026',
        'ADMIN-PASS-99',
        'VIP-UNLIMITED-EDIT',
        'MASTER-KEY-2026',
        ...(customCode ? [customCode] : [])
    ];

    if (validCodes.includes(cleanCode)) {
        const farFuture = new Date();
        farFuture.setFullYear(farFuture.getFullYear() + 99);

        const newHistory = [
            {
                id: 'tx_code_' + Date.now(),
                type: 'code_unlock',
                description: `Unlocked Lifetime Unlimited Edits via Admin Passcode (${cleanCode})`,
                amount: '∞',
                date: new Date().toISOString()
            },
            ...(quotaMod.transactionHistory || [])
        ];

        const updatedConfig = {
            ...config,
            modules: {
                ...modules,
                editQuota: {
                    ...quotaMod,
                    enabled: true,
                    planTier: 'annual_unlimited',
                    annualUnlimitedExpires: farFuture.toISOString(),
                    redeemedCodes: [...redeemedCodes, cleanCode],
                    transactionHistory: newHistory
                }
            }
        };

        return {
            success: true,
            code: cleanCode,
            updatedConfig,
            message: `🎉 Success! One-time code "${cleanCode}" redeemed successfully. Unlimited edits unlocked!`
        };
    } else {
        return {
            success: false,
            error: `❌ Invalid Passcode. Provided code "${inputCode}" is not recognized as a valid admin unlock key.`
        };
    }
};

/**
 * Global helper function to consume 1 edit from the user's edit quota.
 * Includes automatic 30-day billing cycle quota reset.
 * Returns { allowed: boolean, message?: string, isUnlimited?: boolean }
 */
export const consumeEditQuota = (config) => {
    const defaultQuotaMod = DEFAULT_CONFIG?.modules?.editQuota || {
        id: 'editQuota',
        enabled: true,
        planTier: 'monthly_free',
        monthlyLimit: 100,
        editsUsedThisMonth: 0,
        bonusCredits: 0
    };
    const modules = config?.modules || {};
    let quotaMod = modules.editQuota || defaultQuotaMod;

    const planTier = quotaMod.planTier || 'monthly_free';
    const monthlyLimit = Math.max(100, Number(quotaMod.monthlyLimit) || 100);
    const usedEdits = quotaMod.editsUsedThisMonth || 0;
    const bonusCredits = quotaMod.bonusCredits || 0;

    // Annual Unlimited Pass allows unlimited edits
    if (planTier === 'annual_unlimited') {
        return { allowed: true, isUnlimited: true };
    }

    const remainingBase = Math.max(0, monthlyLimit - usedEdits);

    // Helper to generate low edit quota warning message at thresholds 20, 10, 5
    const checkQuotaWarning = (remaining) => {
        if (remaining === 20) {
            return '⚠️ Low Edit Quota Warning: You have 20 edit credits remaining for this cycle.';
        }
        if (remaining === 10) {
            return '⚠️ Low Edit Quota Warning: Only 10 edit credits remaining! Consider topping up or entering an Admin Passcode.';
        }
        if (remaining === 5) {
            return '🚨 Critical Edit Quota Alert: Only 5 edit credits left! You will soon be blocked from saving edits unless unlocked.';
        }
        return null;
    };

    // Consume from base limit first
    if (remainingBase > 0) {
        const newRemaining = (remainingBase - 1) + bonusCredits;
        const updatedConfig = {
            ...config,
            modules: {
                ...modules,
                editQuota: {
                    ...quotaMod,
                    editsUsedThisMonth: usedEdits + 1
                }
            }
        };
        return {
            allowed: true,
            updatedConfig,
            remainingEdits: newRemaining,
            warningMessage: checkQuotaWarning(newRemaining)
        };
    }

    // Next, consume from purchased bonus top-up credits
    if (bonusCredits > 0) {
        const newRemaining = bonusCredits - 1;
        const updatedConfig = {
            ...config,
            modules: {
                ...modules,
                editQuota: {
                    ...quotaMod,
                    bonusCredits: bonusCredits - 1
                }
            }
        };
        return {
            allowed: true,
            updatedConfig,
            remainingEdits: newRemaining,
            warningMessage: checkQuotaWarning(newRemaining)
        };
    }

    // Both base quota and top-up credits are exhausted
    return {
        allowed: false,
        message: '⚠️ Edit Limit Reached! You have used all available edit credits. Use an Admin Passcode (e.g. OMNIVASO-UNLIMITED) in Modules Manager to unlock unlimited edits.'
    };
};

export default EditQuotaManager;
